import axios, {AxiosResponse} from "axios";
import Employment from "@/model/Employment";
import Employee from "@/model/Employee";
import {PagedResult, SpringPagedResult} from "@/service/PagedResult";
import {EmployeeStatus, EmploymentType, WageType} from "@/enums";
import EmployeeName from "@/model/EmployeeName";

const PATH = "/employees";
const PATH_V2 = "/v2/employees";
const PATH_V3 = "/v3/employees";

/**
 * The projections the v3 employee API can return an employee as.
 */
export type EmployeeProjection = "FULL" | "SHALLOW";

/**
 * The filter criteria the v3 employee API supports. `employeeNumber` and `wage` match values starting with
 * their digits; `employmentType`, `wage` and `wageType` refer to the employment and wage valid today.
 */
export interface EmployeeV3Filter {
  term?: string | null;
  status?: EmployeeStatus | null;
  operatorNumber?: number | null;
  employeeNumber?: number | null;
  firstName?: string | null;
  lastName?: string | null;
  employmentType?: EmploymentType | null;
  wage?: number | null;
  wageType?: WageType | null;
}

/**
 * The shape every v3 employee response shares: a uuid `id` (not the numeric database id) plus the employee
 * number. `SHALLOW` responses only ever carry these fields.
 */
export interface EmployeeV3Dto {
  id: string;
  employeeNumber: number;
  firstName: string;
  lastName: string;
  status?: EmployeeStatus;
  birthdate?: string;
  email?: string;
  companyLocation?: { id: string; name: string } | null;
  contractExpiryDate?: string | null;
  export?: boolean;
  temporary?: boolean;
  manager?: boolean;
  teamleadSince?: string | null;
  operatorNumber?: number | null;
  inventoryBonusEligible?: boolean;
  inventoryBonusEligibleSince?: string | null;
  currentEmployment?: EmploymentPeriodV3Dto | null;
  currentWage?: WagePeriodV3Dto | null;
}

/**
 * One wage period as the v3 employee API returns it. Unlike the v1 {@link Wage}, its `id` is a uuid and the
 * wage is called `amount`.
 */
export interface WagePeriodV3Dto {
  id: string;
  amount: number;
  wageType: WageType;
  targetHours: number | null;
  validFrom: string;
  validTo: string | null;
}

/**
 * The body to create a wage period via the v3 employee API. `validFrom` is the month the wage period starts in
 * (`YYYY-MM`); it starts on the first day of that month.
 */
export interface WagePeriodV3Request {
  amount: number;
  wageType: WageType;
  targetHours: number | null;
  validFrom: string;
}

/**
 * The body to update a wage period via the v3 employee API. The start and end date can not be changed, and a
 * `null` `targetHours` clears the target hours.
 */
export interface WagePeriodV3UpdateRequest {
  amount: number;
  wageType: WageType;
  targetHours: number | null;
}

/**
 * The body to reactivate an employee via the v3 employee API. `contractExpiryDate` must be a full date
 * (`YYYY-MM-DD`).
 */
export interface EmployeeReactivateV3Request {
  wage: WagePeriodV3Request;
  contractExpiryDate: string | null;
}

/**
 * One employment period as the v3 employee API returns it. Unlike the v1 {@link Employment}, its `id` is a uuid.
 */
export interface EmploymentPeriodV3Dto {
  id: string;
  employmentType: EmploymentType;
  validFrom: string;
  validTo: string | null;
}

/**
 * The per diems of an employee on one day as the v3 employee API returns them. Unlike the v1/v2 per diem, its
 * `id` is a uuid and the order is referenced by its uuid and name only.
 */
export interface EmployeePerDiemV3Dto {
  id: string;
  date: string;
  order: { id: string; name: string };
  catering: number;
  misc: number;
  breakfast: boolean;
  cateringAfterBreakfast: number;
}

/**
 * The per diems to record for an employee on one day via the v3 employee API. The order is referenced by its uuid
 * and must be one the employee logged time on that day. Both per diems zero and no breakfast is rejected; a per
 * diem is removed with {@link deletePerDiemV3} instead.
 */
export interface EmployeePerDiemV3UpdateRequest {
  orderId: string;
  catering: number;
  misc: number;
  breakfast: boolean;
}

/**
 * A new per diem for an employee via the v3 employee API: an {@link EmployeePerDiemV3UpdateRequest} plus its day.
 */
export interface EmployeePerDiemV3Request extends EmployeePerDiemV3UpdateRequest {
  date: string;
}

/**
 * Maps a v3 employee DTO onto an {@link EmployeeName} so callers do not need to know the v3 field names.
 * The numeric database `id` is not exposed by v3, so it is left undefined; use `uuid` to identify the
 * employee for further v3 calls, and `employeeId` for everything else (routing, v1/v2 calls, ...).
 */
function toEmployeeName(dto: EmployeeV3Dto): EmployeeName {
  const employeeName = new EmployeeName();
  employeeName.uuid = dto.id;
  employeeName.employeeId = dto.employeeNumber;
  employeeName.firstName = dto.firstName;
  employeeName.lastName = dto.lastName;
  return employeeName;
}

/**
 * One row of the employee table/list, flattened from a `FULL` v3 employee DTO into the field names the
 * v1/v2 APIs used. `wage` and `wageType` are null when the caller may not see the employee's wage.
 */
export interface EmployeeListItem {
  uuid: string;
  employeeId: number;
  firstName: string;
  lastName: string;
  active: boolean;
  wage: number | null;
  wageType: WageType | null;
  employmentType: EmploymentType | null;
  contractExpiryDate: string | null;
  operatorNumber: number | null;
}

function toEmployeeListItem(dto: EmployeeV3Dto): EmployeeListItem {
  return {
    uuid: dto.id,
    employeeId: dto.employeeNumber,
    firstName: dto.firstName,
    lastName: dto.lastName,
    active: dto.status === EmployeeStatus.ACTIVE,
    wage: dto.currentWage?.amount ?? null,
    wageType: dto.currentWage?.wageType ?? null,
    employmentType: dto.currentEmployment?.employmentType ?? null,
    contractExpiryDate: dto.contractExpiryDate ?? null,
    operatorNumber: dto.operatorNumber ?? null
  };
}

export default {
  /**
   * Finds employees via the v3 API. `sort` accepts employeeNumber, firstName, lastName, wage,
   * contractExpiryDate and operatorNumber; sorting by wage is forbidden for callers who may not see wages.
   */
  findEmployeesV3(filter: EmployeeV3Filter | null, projection: EmployeeProjection, size: number | null, page: number | null, sort: string | null): Promise<AxiosResponse<SpringPagedResult<EmployeeV3Dto>>> {
    return axios.get(`${PATH_V3}`, {
      params: {
        term: filter?.term,
        status: filter?.status,
        operatorNumber: filter?.operatorNumber,
        employeeNumber: filter?.employeeNumber,
        firstName: filter?.firstName,
        lastName: filter?.lastName,
        employmentType: filter?.employmentType,
        wage: filter?.wage,
        wageType: filter?.wageType,
        projection: projection,
        size: size ? size : 250,
        page: page ? page : 0,
        sort: sort ? sort : null
      }
    });
  },
  /**
   * Finds employees via the v3 API for the employee table/list, already mapped to {@link EmployeeListItem}s.
   */
  findEmployeeListItemsV3(filter: EmployeeV3Filter | null, size: number | null, page: number | null, sort: string | null): Promise<SpringPagedResult<EmployeeListItem>> {
    return this.findEmployeesV3(filter, "FULL", size, page, sort)
      .then(response => ({
        content: response.data.content.map(toEmployeeListItem),
        page: response.data.page
      }));
  },
  /**
   * Finds employees by a search term via the v3 API, returning already-mapped {@link EmployeeName}s. Uses the
   * `SHALLOW` projection, which only returns the id, employeeNumber, firstName and lastName.
   */
  findByTermV3(term: string): Promise<Array<EmployeeName>> {
    return this.findEmployeesV3({term: term}, "SHALLOW", 10, 0, null)
      .then(response => response.data.content.map(toEmployeeName));
  },
  /**
   * Gets one employee via the v3 API by its uuid (not its numeric employeeId - resolve the uuid first, e.g.
   * from a v1/v2 response's `uuid` field, or from {@link findEmployeesV3}/{@link findByTermV3}).
   */
  getEmployeeV3(uuid: string, projection: EmployeeProjection): Promise<AxiosResponse<EmployeeV3Dto>> {
    return axios.get(`${PATH_V3}/${uuid}`, {
      params: {
        projection: projection
      }
    });
  },
  /**
   * Gets all wage periods of an employee via the v3 API by its uuid, newest first by start date. The list is
   * empty when the caller may not see the employee's wages.
   */
  getEmployeeWagesV3(uuid: string): Promise<Array<WagePeriodV3Dto>> {
    return axios.get(`${PATH_V3}/${uuid}/wages`)
      .then(response => response.data.wages);
  },
  /**
   * Starts a new wage period via the v3 API. The wage period covering the day before ends that day.
   */
  createWageV3(uuid: string, wage: WagePeriodV3Request): Promise<WagePeriodV3Dto> {
    return axios.post(`${PATH_V3}/${uuid}/wages`, wage)
      .then(response => response.data);
  },
  /**
   * Replaces the amount, wage type and target hours of a wage period via the v3 API.
   */
  updateWageV3(uuid: string, wageId: string, wage: WagePeriodV3UpdateRequest): Promise<WagePeriodV3Dto> {
    return axios.put(`${PATH_V3}/${uuid}/wages/${wageId}`, wage)
      .then(response => response.data);
  },
  /**
   * Deletes a wage period via the v3 API. The wage period covering the day before takes over its end date.
   */
  deleteWageV3(uuid: string, wageId: string) {
    return axios.delete(`${PATH_V3}/${uuid}/wages/${wageId}`);
  },
  /**
   * Gets all employment periods of an employee via the v3 API by its uuid, newest first by start date.
   */
  getEmployeeEmploymentsV3(uuid: string): Promise<Array<EmploymentPeriodV3Dto>> {
    return axios.get(`${PATH_V3}/${uuid}/employments`)
      .then(response => response.data.employments);
  },
  getDefaultData(registrationUuid?: string): Promise<AxiosResponse<Employee>> {
    return axios.get(`${PATH}/default`, {
      params: {
        registrationUuid: registrationUuid
      }
    });
  },
  getEmployeeById(employeeId: number): Promise<AxiosResponse<Employee>> {
    return axios.get(`${PATH}/${employeeId}`);
  },
  getEmployeeV2(employeeId: number): Promise<AxiosResponse<EmployeeName>> {
    return axios.get(`${PATH_V2}/${employeeId}`);
  },
  createEmployee(employee: Employee, registrationUuid?: string): Promise<AxiosResponse<Employee>> {
    return axios.post(`${PATH}`, {
      employeeId: employee.employeeId,
      firstName: employee.firstName,
      lastName: employee.lastName,
      birthdate: employee.birthdate,
      email: employee.email,
      companyLocation: employee.companyLocation,
      contractExpiryDate: employee.contractExpiryDate,
      export: employee.export,
      employment: employee.employments[0],
      wage: employee.wages[0],
      operatorNumber: employee.operatorNumber,
      inventoryBonusEligible: employee.inventoryBonusEligible,

      registrationUuid: registrationUuid
    }, {
      headers: {
        "Content-Type": "application/json"
      }
    });
  },
  createWelcomePdf(employeeId: number) {
    return axios.post(`${PATH}/${employeeId}/welcome`, {}, {
      responseType: "blob"
    });
  },
  updateEmployee(employeeId: number, payload: Employee) {
    return axios.put(`${PATH}/${employeeId}`, payload);
  },
  updateEmployeePatch(employeeId: number, payload: Employee) {
    return axios.patch(`${PATH}/${employeeId}`, payload, {
      headers: {
        "Content-Type": "application/merge-patch+json"
      }
    });
  },
  /**
   * Deactivates an employee via the v3 API by its uuid. Answers 204 on success and 409 if the employee is
   * already inactive.
   */
  deactivateEmployee(uuid: string) {
    return axios.post(`${PATH_V3}/${uuid}/deactivate`);
  },
  /**
   * Reactivates an employee via the v3 API by its uuid and starts the given wage period, ending the previous open
   * one the day before. Answers 409 if the employee is already active.
   */
  reactivateEmployee(uuid: string, request: EmployeeReactivateV3Request): Promise<EmployeeV3Dto> {
    return axios.post(`${PATH_V3}/${uuid}/reactivate`, request)
      .then(response => response.data);
  },
  createEmployment(employeeId: number, employment: Employment): Promise<AxiosResponse<Employment>> {
    return axios.post(`${PATH}/${employeeId}/employments`, employment);
  },
  updateEmployment(employeeId: number, employment: Employment): Promise<AxiosResponse<Employment>> {
    return axios.patch(`${PATH}/${employeeId}/employments/${employment.id}`, employment);
  },
  deleteEmploymentById(employeeId: number, employmentId: number) {
    return axios.delete(`${PATH}/${employeeId}/employments/${employmentId}`);
  },
  getEmployeeWorklogsV2(employeeId: number, yearMonth: string): Promise<AxiosResponse<PagedResult>> {
    return axios.get(`${PATH_V2}/${employeeId}/worklogs`, {
      params: {
        yearMonth: yearMonth
      }
    });
  },
  /**
   * Gets an employee's per diems of a month via the v3 API by its uuid, oldest first.
   */
  getEmployeePerDiemsV3(uuid: string, yearMonth: string): Promise<Array<EmployeePerDiemV3Dto>> {
    return axios.get(`${PATH_V3}/${uuid}/per-diems`, {
      params: {
        month: yearMonth
      }
    })
      .then(response => response.data.perDiems);
  },
  /**
   * Records a per diem for an employee via the v3 API. The employee must not have one on that day yet.
   */
  createPerDiemV3(uuid: string, perDiem: EmployeePerDiemV3Request): Promise<EmployeePerDiemV3Dto> {
    return axios.post(`${PATH_V3}/${uuid}/per-diems`, perDiem)
      .then(response => response.data);
  },
  /**
   * Replaces the order, amounts and breakfast of an employee's per diem via the v3 API. Its day stays as it is.
   */
  updatePerDiemV3(uuid: string, perDiemId: string, perDiem: EmployeePerDiemV3UpdateRequest): Promise<EmployeePerDiemV3Dto> {
    return axios.put(`${PATH_V3}/${uuid}/per-diems/${perDiemId}`, perDiem)
      .then(response => response.data);
  },
  /**
   * Deletes an employee's per diem via the v3 API.
   */
  deletePerDiemV3(uuid: string, perDiemId: string): Promise<AxiosResponse<void>> {
    return axios.delete(`${PATH_V3}/${uuid}/per-diems/${perDiemId}`);
  },
  getCountingPerformances(employeeId: number, yearMonth: string): Promise<AxiosResponse<PagedResult>> {
    return axios.get(`${PATH_V2}/${employeeId}/counting-performances`, {
      params: {
        size: 1000,
        yearMonth: yearMonth
      }
    });
  }
};
